// Web half of theming. The other half is `domain/theme` in portal-core.
//
// The split follows §5.1's rule: what to compute is shared, how to apply it is
// not. Resolving (tenant branding + scheme) → tokens is arithmetic that runs
// identically everywhere, so it lives in core. Writing those tokens onto a
// document, listening to a media query, and injecting a font `<link>` are
// browser operations, so they live here — the same place as the other web
// adapters.
//
// This is deliberately NOT a fifth port. Persisting the customer's choice
// already goes through StoragePort, and the rest is three functions the native
// app will write against Appearance and NativeWind. A port would be ceremony
// around a one-line difference.

import { themeToCssVariables, type ColorScheme, type ThemeTokens } from '@invoiceflow/portal-core';

/** What the OS says, right now. */
export function getSystemScheme(): ColorScheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Fires when the OS setting changes. Returns an unsubscribe. */
export function onSystemSchemeChange(callback: (scheme: ColorScheme) => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};

  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (event: MediaQueryListEvent): void => {
    callback(event.matches ? 'dark' : 'light');
  };

  query.addEventListener('change', handler);
  return () => query.removeEventListener('change', handler);
}

/**
 * Writes the resolved palette onto the document.
 *
 * The `dark` class is what Tailwind's `darkMode: 'class'` keys off, and
 * `color-scheme` is what tells the browser to render scrollbars, form controls
 * and the caret on the right side — a themed page with light-mode scrollbars is
 * the giveaway that only half the job was done.
 */
export function applyTheme(tokens: ThemeTokens, scheme: ColorScheme): void {
  const root = document.documentElement;

  for (const [name, value] of Object.entries(themeToCssVariables(tokens))) {
    root.style.setProperty(name, value);
  }

  root.classList.toggle('dark', scheme === 'dark');
  root.style.colorScheme = scheme;
}

// ─── Tenant font ─────────────────────────────────────────────────────────────

const FONT_ATTR = 'data-google-font';

function isFontLoaded(fontFamily: string): boolean {
  return document.head.querySelector(`link[${FONT_ATTR}="${CSS.escape(fontFamily)}"]`) !== null;
}

/** Adds the tenant's Google Font once. Idempotent across re-renders. */
export function loadGoogleFont(fontFamily: string): void {
  if (!fontFamily || isFontLoaded(fontFamily)) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
    /\s+/g,
    '+'
  )}:wght@400;500;600;700&display=swap`;
  link.setAttribute(FONT_ATTR, fontFamily);
  document.head.appendChild(link);
}

export function applyFontFamily(fontFamily: string | undefined): void {
  if (!fontFamily) return;
  loadGoogleFont(fontFamily);
  document.documentElement.style.setProperty(
    '--font-family',
    `'${fontFamily}', system-ui, sans-serif`
  );
}
